import { Box, ButtonGroup, ControlPrefix, FormControlSizes, HStack, SearchSelect, ToggleButton } from '@talos/kyoko';
import { AggregatedAssetSelect } from '../AggregatedMetric/AggregatedAssetSelect';
import { ASSET_CONTROL_WIDTH } from '../core';
import { FundingRateBlotterView, FundingRateGroupBy } from './FundingRateBlotter.types';
import { FUNDING_RATE_BLOTTER_VIEW_LABELS, FUNDING_RATE_GROUP_BY_LABELS } from './tokens';

const GROUP_BY_OPTIONS: FundingRateGroupBy[] = [
  FundingRateGroupBy.None,
  FundingRateGroupBy.Asset,
  FundingRateGroupBy.Exchange,
];

interface FundingRateBlotterToolbarProps {
  view: FundingRateBlotterView;
  onViewChange: (view: FundingRateBlotterView) => void;
  groupBy: FundingRateGroupBy;
  onGroupByChange: (groupBy: FundingRateGroupBy) => void;
  availableTalosSymbols: string[];
  selectedTalosSymbols: string[];
  onSelectedTalosSymbolsChange: (talosSymbols: string[]) => void;
}

/**
 * Left-slot toolbar: the List/Matrix view toggle, a Group By dropdown (List view only), and a
 * compact asset multi-select (mirrors the settings panel). Editing assets here is equivalent to
 * editing them in settings — both persist to the same config and re-fetch.
 */
export function FundingRateBlotterToolbar({
  view,
  onViewChange,
  groupBy,
  onGroupByChange,
  availableTalosSymbols,
  selectedTalosSymbols,
  onSelectedTalosSymbolsChange,
}: FundingRateBlotterToolbarProps) {
  return (
    <HStack gap="spacing8">
      <ButtonGroup size={FormControlSizes.Small} data-testid="funding-rate-blotter-view-toggle">
        {Object.values(FundingRateBlotterView).map(option => (
          <ToggleButton
            key={option}
            selected={view === option}
            onClick={() => onViewChange(option)}
            data-testid={`funding-rate-blotter-view-${option.toLowerCase()}`}
          >
            {FUNDING_RATE_BLOTTER_VIEW_LABELS[option]}
          </ToggleButton>
        ))}
      </ButtonGroup>
      {view === FundingRateBlotterView.List && (
        <Box w="180px" flexShrink={0}>
          <SearchSelect
            w="100%"
            size={FormControlSizes.Small}
            selection={groupBy}
            options={GROUP_BY_OPTIONS}
            getLabel={option => FUNDING_RATE_GROUP_BY_LABELS[option]}
            onChange={next => onGroupByChange(next ?? FundingRateGroupBy.None)}
            prefix={<ControlPrefix>Group By</ControlPrefix>}
            portalize
            data-testid="funding-rate-blotter-group-by"
          />
        </Box>
      )}
      <AggregatedAssetSelect
        availableTalosSymbols={availableTalosSymbols}
        selectedTalosSymbols={selectedTalosSymbols}
        onSelectedTalosSymbolsChange={onSelectedTalosSymbolsChange}
        width={ASSET_CONTROL_WIDTH}
        buttonTestID="funding-rate-blotter-toolbar-assets"
        dropdownTestID="funding-rate-blotter-toolbar-assets-dropdown"
      />
    </HStack>
  );
}
